// Tasks API — typed-обёртки над 8 endpoints /api/tasks/*.

import { apiClient } from './client';

/** Возможные статусы задачи (синхронизировано с backend TaskStatus enum). */
export type TaskStatus = 'NEW' | 'IN_PROGRESS' | 'BLOCKED' | 'DONE' | 'CANCELLED';

/** Приоритет задачи. */
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

/** DTO задачи. */
export interface Task {
  id: string;
  /** Сквозной номер задачи (backend R18, TaskDto.taskNumber). */
  taskNumber: number;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeUserId: string | null;
  departmentId: string;
  creatorUserId: string;
  /** UUID связанной сделки (nullable, R38a/U-9, V30 FK → deals.id, ON DELETE SET NULL). */
  dealId: string | null;
  dueAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  departmentIds: string[];
}

/** Page response (0-indexed). */
export interface Page<T> {
  content: T[];
  total: number;
  page: number;
  size: number;
}

/** Фильтры для GET /api/tasks. */
export interface TaskFilters {
  page?: number;
  size?: number;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeUserId?: string;
  departmentId?: string;
  title?: string;
  /** Фильтр по связанной сделке (R38a/U-9: backend param dealId). */
  dealId?: string;
  /** Сортировка «field,direction» (U-23: whitelist createdAt|updatedAt|dueAt; направление asc|desc). */
  sort?: string;
}

export interface TaskCreateRequest {
  title: string;
  description?: string | null;
  priority: TaskPriority;
  assigneeUserId?: string | null;
  departmentId: string;
  dueAt?: string | null;
  /** UUID связанной сделки (nullable, R38a/U-9, V30). */
  dealId?: string | null;
}

export interface TaskUpdateRequest {
  title?: string;
  description?: string | null;
  priority?: TaskPriority;
  assigneeUserId?: string | null;
  dueAt?: string | null;
  /** UUID связанной сделки (nullable, R38a/U-9, V30). */
  dealId?: string | null;
  expectedUpdatedAt: string;
}

export interface TaskChangeStatusRequest {
  /** Целевой статус (контракт backend TaskChangeStatusRequest.newStatus, R31 / U-3). */
  newStatus: TaskStatus;
  /** Ожидаемое updatedAt для optimistic lock (backend @NotNull). */
  expectedUpdatedAt: string;
  /** Audit-комментарий (optional, ≤1000 chars на backend). */
  comment?: string;
}

/**
 * Разрешённые переходы статусов задачи (R31 / U-3).
 * Зеркало state machine backend `TaskStatus.java#canTransitionTo`:
 * NEW → IN_PROGRESS | CANCELLED; IN_PROGRESS → DONE | BLOCKED | CANCELLED;
 * BLOCKED → IN_PROGRESS | CANCELLED; терминальные DONE/CANCELLED — без переходов.
 * UI не предлагает запрещённые переходы (иначе backend отвечает 409).
 */
export const TASK_STATUS_TRANSITIONS: Record<TaskStatus, readonly TaskStatus[]> = {
  NEW: ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['DONE', 'BLOCKED', 'CANCELLED'],
  BLOCKED: ['IN_PROGRESS', 'CANCELLED'],
  DONE: [],
  CANCELLED: [],
};

/** Возвращает список статусов, в которые разрешён переход из {@code current}. */
export function allowedNextStatuses(current: TaskStatus): readonly TaskStatus[] {
  return TASK_STATUS_TRANSITIONS[current];
}

export interface TaskAssignRequest {
  assigneeUserId: string;
}

/** Tasks API. */
export const tasksApi = {
  /** GET /api/tasks?filters → Page<Task>. */
  async list(filters: TaskFilters = {}): Promise<Page<Task>> {
    const { data } = await apiClient.get<Page<Task>>('/api/tasks', { params: filters });
    return data;
  },

  /** GET /api/tasks/{id} → Task. */
  async get(id: string): Promise<Task> {
    const { data } = await apiClient.get<Task>(`/api/tasks/${id}`);
    return data;
  },

  /** POST /api/tasks → Task. */
  async create(payload: TaskCreateRequest): Promise<Task> {
    const { data } = await apiClient.post<Task>('/api/tasks', payload);
    return data;
  },

  /** PUT /api/tasks/{id} → Task. */
  async update(id: string, payload: TaskUpdateRequest): Promise<Task> {
    const { data } = await apiClient.put<Task>(`/api/tasks/${id}`, payload);
    return data;
  },

  /** DELETE /api/tasks/{id}. */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/tasks/${id}`);
  },

  /** POST /api/tasks/{id}/assign → Task. */
  async assign(id: string, payload: TaskAssignRequest): Promise<Task> {
    const { data } = await apiClient.post<Task>(`/api/tasks/${id}/assign`, payload);
    return data;
  },

  /** DELETE /api/tasks/{id}/assign. */
  async unassign(id: string): Promise<Task> {
    const { data } = await apiClient.delete<Task>(`/api/tasks/${id}/assign`);
    return data;
  },

  /** POST /api/tasks/{id}/status → Task. */
  async changeStatus(id: string, payload: TaskChangeStatusRequest): Promise<Task> {
    const { data } = await apiClient.post<Task>(`/api/tasks/${id}/status`, payload);
    return data;
  },
};
